import { zodResolver } from '@hookform/resolvers/zod';
import { useIsMutating } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useForm, useController, useFormState, Controller, SubmitHandler } from 'react-hook-form';
import { z } from 'zod';

import CheckmarkSmallSvg from '@/components/common/CheckmarkSmallSvg';
import CloseSvg from '@/components/common/CloseSvg';
import HidePasswordSvg from '@/components/common/HidePasswordSvg';
import LoadingSvg from '@/components/common/LoadingSvg';
import ViewPasswordSvg from '@/components/common/ViewPasswordSvg';
import {
    isValidContainNumber,
    isValidTooSmall,
    isValidSpecial,
    isValidLower,
    isValidUpper,
    isValidUpperLower,
} from '@/components/myaccount/utils/validation';
import { Button } from '@/components/ui/button';
import { Cookie } from '@/tscapi/utils/cookie';

import { usePasswordInfo, usePasswordUpdate } from '../hooks/useCreatePassword';
import { ChangePasswordSchema } from '../schemas/changepassword.schema';

type ValidationAccountSchema = z.infer<typeof ChangePasswordSchema>;
interface FormChangePasswordProps {
    info?: string;
}
export const FormChangePassword = ({ info }: FormChangePasswordProps) => {
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showRetypePassword, setShowRetypePassword] = useState(false);
    const [showError, setShowError] = useState('');
    const toggleShowNewPassword = () => setShowNewPassword((prevState) => !prevState);
    const toggleShowRetypePassword = () => setShowRetypePassword((prevState) => !prevState);
    const mutationPasswordInfo = usePasswordInfo();
    const mutationPasswordUpdate = usePasswordUpdate();
    const isMutating = useIsMutating();
    const {
        handleSubmit,
        register,
        control,
        setError,
        watch,
        trigger,
        setValue,
        clearErrors,
        getFieldState,
        formState: { errors, isDirty, isValid, dirtyFields, touchedFields, isSubmitting },
    } = useForm<ValidationAccountSchema>({
        resolver: zodResolver(ChangePasswordSchema),
        mode: 'all',
        criteriaMode: 'all',
        defaultValues: {
            newPassword: '',
            retypeNewPassword: '',
        },
    });
    const passw = watch('retypeNewPassword');
    useEffect(() => {
        if (info) {
            console.log(`info: ${info}`);
            const payload = {
                Info: info,
            };
            mutationPasswordInfo.mutate(payload, {
                onSuccess: ({ data }) => {
                    setShowError('');
                },
                onError: (data) => {
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                    });
                    setShowError(data as string);
                },
            });
        }
    }, [info]);

    const onSubmit = (data) => {
        const payload = {
            NewPassword: data.newPassword,
            NewPasswordConfirmed: data.retypeNewPassword,
            Info: info,
        };
        mutationPasswordUpdate.mutate(payload, {
            onSuccess: (data) => {
                setShowError('');
                const cookie: Cookie = new Cookie();
                cookie.deleteTargetUrl();
                window.location.href = '/pages/myaccount';
            },
            onError: (data) => {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                });
                setShowError(data as string);
            },
        });
    };
    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="px-5 pb-32 pt-14 md:mx-auto md:max-w-[560px]">
                <h1 className="m-0 pb-2 text-xl font-bold text-[#1a1a1a] md:pb-4 md:text-center md:text-2xl">
                    Create a new password
                </h1>
                {showError ? (
                    <>
                        <p className="text-base font-bold text-red-600">{showError}</p>
                    </>
                ) : null}
                <p className="mb-10 text-base font-light">Please enter your new password below.</p>
                <div className="mb-4 pb-4">
                    {/* --------- new password --------- */}
                    <div className="relative z-0 mb-6 flex flex-col justify-center">
                        <input
                            className="profile__form-input peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                            type={showNewPassword ? 'text' : 'password'}
                            maxLength={16}
                            placeholder=" "
                            data-error={getFieldState('newPassword').error ? 'true' : 'false'}
                            // data-empty={inputNew === '' ? 'true' : 'false'}
                            tabIndex={0}
                            autoComplete="new-password"
                            {...register('newPassword', {
                                onChange: (e) => {
                                    if (passw != '') {
                                        trigger('retypeNewPassword');
                                    }
                                },
                            })}
                            data-qm-block="true"
                        />
                        <label
                            className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                            htmlFor="newPassword"
                        >
                            New password
                        </label>

                        <button
                            className="absolute right-0 z-[2]"
                            type="button"
                            onClick={toggleShowNewPassword}
                            tabIndex={-1}
                        >
                            {showNewPassword ? (
                                <ViewPasswordSvg width="20" height="19" />
                            ) : (
                                <HidePasswordSvg width="20" height="20" />
                            )}
                        </button>
                    </div>

                    {(getFieldState('newPassword').isTouched || getFieldState('newPassword').isDirty) && (
                        <div className="px-5 pb-7 pt-0 text-[13px]">
                            <div className="font-semibold">Your password must have:</div>
                            <div className="flex flex-col">
                                <div
                                    className={
                                        isValidTooSmall(errors)
                                            ? 'valid group pt-2 text-green-700'
                                            : 'group pt-2 text-red-500'
                                    }
                                >
                                    <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                    <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                    <span className="pl-2.5">at least 8 characters</span>
                                </div>
                                <div
                                    className={
                                        isValidUpper(errors)
                                            ? 'valid group pt-2 text-green-700'
                                            : 'group pt-2 text-red-500'
                                    }
                                >
                                    <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                    <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                    <span className="pl-2.5">include at least 1 upper case letter</span>
                                </div>
                                <div
                                    className={
                                        isValidLower(errors)
                                            ? 'valid group pt-2 text-green-700'
                                            : 'group pt-2 text-red-500'
                                    }
                                >
                                    <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                    <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                    <span className="pl-2.5">include at least 1 lower case letter</span>
                                </div>
                                <div
                                    className={
                                        isValidContainNumber(errors)
                                            ? 'valid group pt-2 text-green-700'
                                            : 'group pt-2 text-red-500'
                                    }
                                >
                                    <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                    <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                    <span className="pl-2.5">include at least 1 number</span>
                                </div>
                                <div
                                    className={
                                        isValidSpecial(errors)
                                            ? 'valid group pt-2 text-green-700'
                                            : 'group pt-2 text-red-500'
                                    }
                                >
                                    <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                    <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                    <span className="pl-2.5">
                                        include at least 1 special character
                                        <span className="max-md:hidden md:inline">
                                            {' '}
                                            (e.g. !, @, #, $, %, ^, &, *, or ~)
                                        </span>
                                        <span className="max-md:block max-md:pl-6 md:hidden">
                                            (e.g. !, @, #, $, %, ^, &, *, or ~)
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --------- retype password --------- */}
                    <div className="relative z-0 mb-6 flex flex-col justify-center">
                        <div className="relative z-0 flex flex-col justify-center">
                            <input
                                className="profile__form-input peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                                type={showRetypePassword ? 'text' : 'password'}
                                // data-empty={inputRetype === '' ? 'true' : 'false'}
                                data-error={getFieldState('retypeNewPassword').error ? 'true' : 'false'}
                                tabIndex={0}
                                placeholder=" "
                                maxLength={16}
                                autoComplete="new-password"
                                {...register('retypeNewPassword')}
                                data-qm-block="true"
                            />
                            <label
                                className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                                htmlFor="retypeNewPassword"
                            >
                                Re-type new password
                            </label>

                            <button
                                className="absolute right-0 z-[2]"
                                type="button"
                                onClick={toggleShowRetypePassword}
                                tabIndex={-1}
                            >
                                {showRetypePassword ? (
                                    <ViewPasswordSvg width="20" height="19" />
                                ) : (
                                    <HidePasswordSvg width="20" height="20" />
                                )}
                            </button>
                        </div>
                        {errors.retypeNewPassword && (
                            <div className="mt-1 font-semibold text-[#df0c0c]">{errors.retypeNewPassword.message}</div>
                        )}

                        {/* {mutation.isError ? <div className="profile__error">{mutation.error}</div> : null} */}
                    </div>
                </div>
                {/* --------- Submit --------- */}
                <div>
                    <div className="mb-7 mt-4 grid grid-cols-1">
                        <Button
                            type="submit"
                            aria-label={'Send email'}
                            disabled={!isValid || isSubmitting}
                            tabIndex={!isValid ? -1 : 0}
                        >
                            Change password
                        </Button>
                    </div>
                </div>
            </div>
            {mutationPasswordUpdate.isLoading || isMutating || mutationPasswordInfo.isLoading ? (
                <div className="blockPageWrap" style={{ display: 'block' }}>
                    <div className="blockPageOverlay"></div>
                    <div className="blockMsg">
                        <LoadingSvg />
                    </div>
                </div>
            ) : null}
        </form>
    );
};
